import { listingRequest } from '@/requests/listing';
import { scrollTo } from '@/utils/scroll-to';
import { useRef, useState } from 'react';
import { useNotifications } from './use-notifications';
import { useRequest } from './use-request';
/**
 * Interact with and provide state to the listing components.
 *
 * Handles
 * - Network requests to fetch new items
 * - Filters state (applied and unapplied filters)
 * - Pagination
 */
export function useListings({ searchEndpoint, initialData, facetsToShow, parentPage, allFacetsSelectionMode = 'CheckOtherValues', requestFailedErrorMessage, }) {
    const notifications = useNotifications();
    const containerRef = useRef(null);
    const [selectedPage, setSelectedPage] = useState(1);
    const [selectedQuery, setSelectedQuery] = useState();
    // Unapplied selected filters are filters that have been selected but not yet applied
    const [unappliedSelectedFilterValues, setUnappliedSelectedFilterValues] = useState({});
    // Selected filters are filters that have been applied
    const [selectedFilterValues, setSelectedFilterValues] = useState({});
    const response = useRequest({
        request: async () => {
            try {
                const response = await listingRequest(searchEndpoint, {
                    facetsToShow,
                    parentPage,
                    query: selectedQuery,
                    facetValues: selectedFilterValues,
                    pageSize: initialData.pageSize,
                    from: (selectedPage - 1) * initialData.pageSize,
                });
                return response;
            }
            catch (error) {
                notifications.push('error', requestFailedErrorMessage ?? 'Failed to load page. Please try again.');
                throw error;
            }
        },
        initial: initialData,
        deps: [selectedFilterValues, selectedPage, selectedQuery],
    });
    const totalPages = response ? Math.ceil(response.total / response.pageSize) : 1;
    const toggleFilterValue = (filter, value, autoApply = false) => {
        const entries = Object.entries(unappliedSelectedFilterValues);
        const currentEntryValue = entries.find(([key]) => key === filter.key)?.[1] ?? [];
        const updateEntryValue = (entries) => {
            if (allFacetsSelectionMode === 'CheckOtherValues') {
                if (value === 'all' && entries.length === filter.values.length) {
                    return [];
                }
                if (value === 'all') {
                    return filter.values.map((v) => v.key);
                }
                if (entries.includes(value.key)) {
                    return entries.filter((v) => v !== value.key);
                }
                return [...entries, value.key];
            }
            if (allFacetsSelectionMode === 'UncheckOtherValues') {
                if (value === 'all' || (entries.length + 1 === filter.values.length && !entries.includes(value.key))) {
                    return [];
                }
                if (entries.includes(value.key)) {
                    return entries.filter((v) => v !== value.key);
                }
                return [...entries, value.key];
            }
            return entries;
        };
        const updatedFilters = {
            ...Object.fromEntries(entries.filter(([key]) => key !== filter.key)),
            [filter.key]: updateEntryValue(currentEntryValue),
        };
        setUnappliedSelectedFilterValues(updatedFilters);
        if (autoApply) {
            setSelectedPage(1);
            setSelectedFilterValues(updatedFilters);
            scrollToTopOfComponent();
        }
    };
    const clearFilters = (autoApply = false) => {
        setUnappliedSelectedFilterValues({});
        if (autoApply) {
            setSelectedPage(1);
            setSelectedFilterValues({});
            scrollToTopOfComponent();
        }
    };
    const scrollToTopOfComponent = () => {
        // Avoid using scrollIntoView as if the contents of the results
        // change during the scroll it will jump to the wrong place
        scrollTo(containerRef.current, { accountForHeader: true, behavior: 'instant' });
    };
    const nextPage = () => {
        let targetPage = selectedPage + 1;
        if (targetPage > totalPages) {
            targetPage = 1;
        }
        setSelectedPage(targetPage);
        scrollToTopOfComponent();
    };
    const previousPage = () => {
        let targetPage = selectedPage - 1;
        if (targetPage < 1) {
            targetPage = totalPages;
        }
        setSelectedPage(targetPage);
        scrollToTopOfComponent();
    };
    const applyFilters = () => {
        setSelectedPage(1);
        setSelectedFilterValues(unappliedSelectedFilterValues);
        scrollToTopOfComponent();
    };
    const search = (value) => {
        setSelectedPage(1);
        setSelectedQuery(value);
        scrollToTopOfComponent();
    };
    const isFilterValueSelected = (filter, value) => {
        const values = unappliedSelectedFilterValues[filter.key] ?? [];
        if (value === 'all') {
            if (values.length === filter.values.length || values.length === 0) {
                return true;
            }
            return 'partial';
        }
        return values.includes(value.key);
    };
    const appliedFiltersValuesCount = Object.values(selectedFilterValues).reduce((acc, values) => acc + values.length, 0);
    const key = response.items.map((item) => item.dataObject.instanceID).join(',');
    return {
        containerRef,
        items: response?.items ?? [],
        filters: response?.filters ?? [],
        currentPage: selectedPage,
        totalPages,
        applyFilters,
        toggleFilterValue,
        isFilterValueSelected,
        clearFilters,
        search,
        selectedQuery,
        nextPage,
        previousPage,
        appliedFiltersValuesCount,
        notifications,
        key,
    };
}
